"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { Button } from "@/components/ui/button";
import { Users, Package, CreditCard } from "lucide-react";

export default function SchoolDetails() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.schoolsStats, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const schools = res.data?.data?.schools || [];
        const found = schools.find((s) => String(s._id) === String(schoolId));
        if (!found) {
          setError("Customer not found");
          setSchool(null);
          return;
        }
        setSchool(found);
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load customer details");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [schoolId]);

  const stats = useMemo(() => {
    const s = school?.stats || {};
    return {
      totalUsers: s.totalUsers || 0,
      totalStaff: s.totalStaff || 0,
    };
  }, [school]);

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading customer details...</div>;
  }

  if (error) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="outline" onClick={() => navigate("/dashboard/schools")}>
          Back to Manage Customers
        </Button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>
      </div>
    );
  }

  if (!school) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{school.name}</h1>
          <p className="text-sm text-gray-500">{school.domain}</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard/schools")}>
          Back to Manage Customers
        </Button>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Customer Code</p>
            <p className="font-semibold text-gray-900">{school.schoolcode || "-"}</p>
          </div>
          <div>
            <p className="text-gray-500">Status</p>
            <p className="font-semibold text-gray-900">{school.isActive !== false ? "Active" : "Inactive"}</p>
          </div>
          <div>
            <p className="text-gray-500">Email</p>
            <p className="font-semibold text-gray-900">{school.email || "-"}</p>
          </div>
          <div>
            <p className="text-gray-500">Phone</p>
            <p className="font-semibold text-gray-900">{school.phone || "-"}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard label="Total Users" value={stats.totalUsers} icon={Users} color="text-indigo-600" />
        <MetricCard label="Total Staff" value={stats.totalStaff} icon={Users} color="text-slate-600" />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
            <Package className="h-4 w-4 text-blue-600" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Inventory Activity</h2>
            <p className="text-xs text-gray-500">Stock movement summary and recent transactions for this customer.</p>
          </div>
        </div>
        <Button onClick={() => navigate(`/dashboard/schools/${schoolId}/inventory`)}>View Inventory Activity</Button>
      </div>

      {school.tenantType === "industry" && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-500/10">
              <CreditCard className="h-4 w-4 text-green-600" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Billing</h2>
              <p className="text-xs text-gray-500">Plan, subscription, payments and invoices for this customer.</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/dashboard/schools/${schoolId}/billing`)}>View Billing</Button>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="mt-2 text-2xl font-bold text-gray-900">{Number(value || 0).toLocaleString()}</p>
    </div>
  );
}
